import React, { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../firebase';
import ImageWithFallback from './ImageWithFallback';
import { subscribeRealtime } from '../utils/realtime';
import { getCallIceServers, getPresence, sendCallSignal } from '../utils/secureFunctions';

export type CallPeer = { uid: string; name: string; photoURL?: string | null };

const START_EVENT = 'belivah:start-call';
const RING_TIMEOUT_MS = 45_000;

/** Starts a 1:1 voice (or video) call; handled by the globally mounted <CallManager />. */
export function startCall(peer: CallPeer, video = false) {
  window.dispatchEvent(new CustomEvent(START_EVENT, { detail: { peer, video } }));
}

type CallState = {
  id: string;
  peer: CallPeer;
  direction: 'out' | 'in';
  status: 'ringing' | 'connecting' | 'active' | 'ended';
  video: boolean;
  note?: string;
};

const newCallId = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9-]/g, '');

function waitForIce(pc: RTCPeerConnection, timeoutMs = 3000) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise<void>((resolve) => {
    let grace: ReturnType<typeof setTimeout> | null = null;
    const timer = setTimeout(() => done(), timeoutMs);
    function done() {
      clearTimeout(timer);
      if (grace) clearTimeout(grace);
      pc.removeEventListener('icegatheringstatechange', check);
      pc.removeEventListener('icecandidate', onCandidate);
      resolve();
    }
    function check() { if (pc.iceGatheringState === 'complete') done(); }
    // A relay (TURN) candidate works on any network, so there's no need to wait for slower STUN/TCP probes.
    function onCandidate(ev: RTCPeerConnectionIceEvent) {
      if (!grace && ev.candidate && / typ relay /.test(ev.candidate.candidate)) grace = setTimeout(done, 250);
    }
    pc.addEventListener('icegatheringstatechange', check);
    pc.addEventListener('icecandidate', onCandidate);
  });
}

function fmt(sec: number) {
  const m = Math.floor(sec / 60);
  return `${m}:${String(sec % 60).padStart(2, '0')}`;
}

const CallManager: React.FC = () => {
  const [call, setCall] = useState<CallState | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const callRef = useRef<CallState | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localRef = useRef<MediaStream | null>(null);
  const remoteRef = useRef<MediaStream | null>(null);
  const offerRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const ringTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toneRef = useRef<{ ctx: AudioContext; timer: ReturnType<typeof setInterval> } | null>(null);
  const remoteVideoEl = useRef<HTMLVideoElement | null>(null);
  const remoteAudioEl = useRef<HTMLAudioElement | null>(null);
  const localVideoEl = useRef<HTMLVideoElement | null>(null);

  const update = useCallback((next: CallState | null) => {
    callRef.current = next;
    setCall(next);
  }, []);

  const stopTone = useCallback(() => {
    if (!toneRef.current) return;
    clearInterval(toneRef.current.timer);
    toneRef.current.ctx.close().catch(() => undefined);
    toneRef.current = null;
  }, []);

  const playTone = useCallback((incoming: boolean) => {
    stopTone();
    try {
      const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
      const ctx = new Ctx();
      const beep = () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = incoming ? 660 : 440;
        gain.gain.value = 0.08;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + (incoming ? 0.35 : 0.8));
      };
      beep();
      toneRef.current = { ctx, timer: setInterval(beep, incoming ? 1200 : 3000) };
    } catch {
      // audio may be blocked until the user interacts; the on-screen prompt still shows
    }
  }, [stopTone]);

  const cleanup = useCallback((note?: string) => {
    stopTone();
    if (ringTimerRef.current) clearTimeout(ringTimerRef.current);
    ringTimerRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    localRef.current?.getTracks().forEach((t) => t.stop());
    localRef.current = null;
    remoteRef.current = null;
    offerRef.current = null;
    startedAtRef.current = null;
    setMuted(false);
    setCameraOff(false);
    setElapsed(0);
    const current = callRef.current;
    if (!current) return;
    if (note) {
      update({ ...current, status: 'ended', note });
      setTimeout(() => { if (callRef.current?.id === current.id) update(null); }, note.length > 30 ? 4500 : 2200);
    } else {
      update(null);
    }
  }, [stopTone, update]);

  const signal = useCallback((kind: Parameters<typeof sendCallSignal>[0]['kind'], extra: { sdp?: string; durationSec?: number } = {}) => {
    const current = callRef.current;
    if (!current) return Promise.resolve();
    return sendCallSignal({ toUserId: current.peer.uid, callId: current.id, kind, video: current.video, ...extra })
      .then(() => undefined)
      .catch((e) => { console.warn('call-signal-failed', kind, e); });
  }, []);

  const createPeer = useCallback(async (video: boolean) => {
    const audio = { echoCancellation: true, noiseSuppression: true };
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio, video: video ? { facingMode: 'user' } : false });
    } catch (e: any) {
      // Laptops without a usable camera (missing, or busy in another app) still join with audio.
      if (!video || e?.name === 'NotAllowedError') throw e;
      stream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
    }
    localRef.current = stream;
    const { iceServers } = await getCallIceServers().catch(() => ({ iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }] as RTCIceServer[] }));
    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    pc.ontrack = (ev) => {
      const remote = ev.streams[0] || new MediaStream([ev.track]);
      remoteRef.current = remote;
      if (remoteAudioEl.current) remoteAudioEl.current.srcObject = remote;
      if (remoteVideoEl.current) remoteVideoEl.current.srcObject = remote;
    };
    pc.onconnectionstatechange = () => {
      const cur = callRef.current;
      if (!cur) return;
      if (pc.connectionState === 'connected' && cur.status !== 'active') {
        startedAtRef.current = Date.now();
        update({ ...cur, status: 'active' });
      }
      if (pc.connectionState === 'failed') {
        void signal('end');
        cleanup('Call failed. The connection could not be established.');
      }
    };
    return pc;
  }, [cleanup, signal, update]);

  const describeMediaError = (e: unknown) => {
    const name = (e as any)?.name || '';
    if (name === 'NotAllowedError') return 'Please allow microphone (and camera) access for belivah.com in your browser, then try again.';
    if (name === 'NotFoundError') return 'No microphone was found on this device.';
    if (name === 'NotReadableError' || name === 'AbortError') return 'Your microphone or camera is being used by another app. Close it and try again.';
    if (name === 'SecurityError') return 'Calls need a secure (https) connection.';
    return 'Could not start the call. Please try again.';
  };

  const beginOutgoing = useCallback(async (peer: CallPeer, video: boolean) => {
    if (callRef.current) { alert('You are already in a call.'); return; }
    if (!window.RTCPeerConnection || !navigator.mediaDevices?.getUserMedia) {
      alert('Calls are not supported in this browser.');
      return;
    }
    update({ id: newCallId(), peer, direction: 'out', status: 'connecting', video });
    const online = await getPresence([peer.uid]).then((r) => r.presence?.[peer.uid]?.online).catch(() => true);
    if (online === false) {
      cleanup(`${peer.name || 'This person'} is not online right now. Send a message and try again later.`);
      return;
    }
    try {
      const pc = await createPeer(video);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIce(pc);
      if (!callRef.current) return;
      await sendCallSignal({ toUserId: peer.uid, callId: callRef.current.id, kind: 'offer', sdp: pc.localDescription?.sdp || '', video });
      update({ ...callRef.current, status: 'ringing' });
      playTone(false);
      ringTimerRef.current = setTimeout(() => {
        if (callRef.current?.status === 'ringing') {
          void signal('cancel');
          cleanup('No answer');
        }
      }, RING_TIMEOUT_MS);
    } catch (e: any) {
      const msg = String(e?.message || '');
      cleanup();
      if (msg.includes('FRIENDS_REQUIRED')) alert(`You need to be friends with ${peer.name} to call them.`);
      else if (e?.name) alert(describeMediaError(e));
      else alert('Could not start the call. Please try again.');
    }
  }, [cleanup, createPeer, playTone, signal, update]);

  const accept = async () => {
    const cur = callRef.current;
    if (!cur || cur.direction !== 'in' || !offerRef.current) return;
    stopTone();
    if (ringTimerRef.current) clearTimeout(ringTimerRef.current);
    update({ ...cur, status: 'connecting' });
    try {
      const pc = await createPeer(cur.video);
      await pc.setRemoteDescription({ type: 'offer', sdp: offerRef.current });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitForIce(pc);
      await signal('answer', { sdp: pc.localDescription?.sdp || '' });
    } catch (e) {
      void signal('decline');
      cleanup();
      alert(describeMediaError(e));
    }
  };

  const hangUp = () => {
    const cur = callRef.current;
    if (!cur) return;
    if (cur.status === 'ended') { update(null); return; }
    if (cur.direction === 'in' && cur.status === 'ringing') {
      void signal('decline');
      cleanup();
      return;
    }
    if (cur.direction === 'out' && cur.status !== 'active') {
      void signal('cancel');
      cleanup();
      return;
    }
    const durationSec = startedAtRef.current ? Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)) : undefined;
    void signal('end', { durationSec });
    cleanup('Call ended');
  };

  useEffect(() => {
    const onStart = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as { peer: CallPeer; video: boolean };
      if (detail?.peer?.uid) void beginOutgoing(detail.peer, !!detail.video);
    };
    window.addEventListener(START_EVENT, onStart);
    return () => window.removeEventListener(START_EVENT, onStart);
  }, [beginOutgoing]);

  useEffect(() => subscribeRealtime((payload) => {
    if (!payload || payload.type !== 'call' || !auth.currentUser) return;
    const cur = callRef.current;
    const { callId, kind, sdp, from, video } = payload as { callId: string; kind: string; sdp?: string; from: CallPeer; video?: boolean };
    if (kind === 'offer') {
      if (cur && cur.status !== 'ended') {
        void sendCallSignal({ toUserId: from.uid, callId, kind: 'busy' }).catch(() => undefined);
        return;
      }
      offerRef.current = sdp || null;
      update({ id: callId, peer: from, direction: 'in', status: 'ringing', video: !!video });
      playTone(true);
      ringTimerRef.current = setTimeout(() => {
        if (callRef.current?.id === callId && callRef.current.status === 'ringing') cleanup('Missed call');
      }, RING_TIMEOUT_MS);
      return;
    }
    if (!cur || cur.id !== callId) return;
    if (kind === 'answer' && cur.direction === 'out' && pcRef.current && sdp) {
      stopTone();
      if (ringTimerRef.current) clearTimeout(ringTimerRef.current);
      update({ ...cur, status: 'connecting' });
      pcRef.current.setRemoteDescription({ type: 'answer', sdp }).catch(() => cleanup('Call failed'));
    } else if (kind === 'decline') {
      cleanup(`${cur.peer.name} declined the call`);
    } else if (kind === 'busy') {
      cleanup(`${cur.peer.name} is on another call`);
    } else if (kind === 'cancel') {
      cleanup('Missed call');
    } else if (kind === 'end') {
      cleanup('Call ended');
    }
  }), [cleanup, playTone, stopTone, update]);

  useEffect(() => {
    if (call?.status !== 'active') return;
    const t = setInterval(() => {
      if (startedAtRef.current) setElapsed(Math.round((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [call?.status]);

  // Re-attach streams once the media elements mount.
  useEffect(() => {
    if (remoteAudioEl.current && remoteRef.current) remoteAudioEl.current.srcObject = remoteRef.current;
    if (remoteVideoEl.current && remoteRef.current) remoteVideoEl.current.srcObject = remoteRef.current;
    if (localVideoEl.current && localRef.current) localVideoEl.current.srcObject = localRef.current;
  });

  useEffect(() => {
    const onUnload = () => { if (callRef.current && callRef.current.status !== 'ended') hangUp(); };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  });

  if (!call) return null;

  const toggleMute = () => {
    const next = !muted;
    localRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };
  const toggleCamera = () => {
    const next = !cameraOff;
    localRef.current?.getVideoTracks().forEach((t) => { t.enabled = !next; });
    setCameraOff(next);
  };

  const statusText = call.status === 'ended'
    ? call.note || 'Call ended'
    : call.status === 'active'
      ? fmt(elapsed)
      : call.status === 'connecting'
        ? 'Connecting…'
        : call.direction === 'in'
          ? `Incoming ${call.video ? 'video' : 'voice'} call…`
          : 'Ringing…';

  const showVideo = call.video && call.status !== 'ended';

  return (
    <div role="dialog" aria-label="Call" style={styles.overlay}>
      <audio ref={remoteAudioEl} autoPlay playsInline style={{ display: 'none' }} />
      {showVideo && (
        <>
          <video ref={remoteVideoEl} autoPlay playsInline style={styles.remoteVideo} />
          <video ref={localVideoEl} autoPlay playsInline muted style={styles.localVideo} />
        </>
      )}
      <div style={{ ...styles.center, ...(showVideo && call.status === 'active' ? { opacity: 0.85, justifyContent: 'flex-start', paddingTop: 40 } : {}) }}>
        {!(showVideo && call.status === 'active') && (
          <ImageWithFallback src={call.peer.photoURL || ''} alt="" style={styles.avatar} />
        )}
        <div style={styles.name}>{call.peer.name}</div>
        <div style={styles.status}>{statusText}</div>
      </div>
      {call.status !== 'ended' && (
        <div style={styles.controls}>
          {call.direction === 'in' && call.status === 'ringing' ? (
            <>
              <button onClick={hangUp} style={{ ...styles.roundBtn, background: '#E53935' }} aria-label="Decline">✕</button>
              <button onClick={accept} style={{ ...styles.roundBtn, background: '#2E7D32' }} aria-label="Accept">{call.video ? '🎥' : '📞'}</button>
            </>
          ) : (
            <>
              <button onClick={toggleMute} style={{ ...styles.roundBtn, background: muted ? '#fff' : 'rgba(255,255,255,0.18)', color: muted ? '#111' : '#fff' }} aria-label={muted ? 'Unmute' : 'Mute'}>{muted ? '🔇' : '🎙️'}</button>
              {call.video && (
                <button onClick={toggleCamera} style={{ ...styles.roundBtn, background: cameraOff ? '#fff' : 'rgba(255,255,255,0.18)' }} aria-label="Camera">{cameraOff ? '🚫' : '📷'}</button>
              )}
              <button onClick={hangUp} style={{ ...styles.roundBtn, background: '#E53935' }} aria-label="Hang up">✕</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'linear-gradient(160deg, #072B2C 0%, #0B4F51 60%, #12355B 100%)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '48px 16px calc(40px + env(safe-area-inset-bottom))',
    boxSizing: 'border-box',
  },
  center: { position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: 1, justifyContent: 'center', textAlign: 'center' },
  avatar: { width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', border: '3px solid rgba(255,255,255,0.35)', boxShadow: '0 0 0 12px rgba(255,255,255,0.06)' },
  name: { fontSize: 24, fontWeight: 700, textShadow: '0 1px 6px rgba(0,0,0,0.4)' },
  status: { fontSize: 15, opacity: 0.85, textShadow: '0 1px 6px rgba(0,0,0,0.4)' },
  controls: { position: 'relative', zIndex: 2, display: 'flex', gap: 28, alignItems: 'center', justifyContent: 'center' },
  roundBtn: { width: 64, height: 64, borderRadius: '50%', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 18px rgba(0,0,0,0.3)' },
  remoteVideo: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#000', zIndex: 1 },
  localVideo: { position: 'absolute', right: 16, top: 16, width: 110, height: 160, objectFit: 'cover', borderRadius: 12, border: '2px solid rgba(255,255,255,0.6)', zIndex: 3, background: '#000', transform: 'scaleX(-1)' },
};

export default CallManager;
